import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@speeki/global-ui-components';
import { IconManager } from '@speeki/icons';

import { useReactiveVar } from '@apollo/client';
import { isUsingMsTeamsVar } from '@boot/reactiveVariables';

import {
    MoreTextWrapper,
    ShowMoreWrapperElement,
    TextContent,
} from './ShowMoreWrapper.styled';

interface ShowMoreWrapperProps {
    children: ReactNode;
    className?: string;
    testId: string;
}

export const MAX_CONTENT_HEIGHT = 500;
export const MAX_OVERSIZED_CONTENT_HEIGHT = 300;

export const ShowMoreWrapper = ({
    children,
    className,
    testId,
}: ShowMoreWrapperProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [showMoreContent, setShowMoreContent] = useState(false);
    const [contentHeight, setContentHeight] = useState(0);

    const isMsTeams = useReactiveVar(isUsingMsTeamsVar);
    const { t } = useTranslation();

    const getContentHeight = () => {
        setContentHeight(contentRef.current?.offsetHeight || 0);
    };

    const isOversized = contentHeight >= MAX_CONTENT_HEIGHT;

    useEffect(() => {
        window.addEventListener('resize', getContentHeight);
        getContentHeight();

        return () => window.removeEventListener('resize', getContentHeight);
    }, []);

    return (
        <ShowMoreWrapperElement className={className}>
            <TextContent
                isMsTeams={isMsTeams}
                isOversized={isOversized}
                showMoreContent={showMoreContent}
            >
                <div ref={contentRef}>{children}</div>
            </TextContent>
            {isOversized && (
                <MoreTextWrapper
                    data-testid={testId}
                    onClick={() =>
                        setShowMoreContent((prevState) => !prevState)
                    }
                >
                    <Typography.TextBody_14_20 fontWeight="bold">
                        {t(showMoreContent ? 'SHOW_LESS' : 'SHOW_MORE')}{' '}
                    </Typography.TextBody_14_20>
                    <IconManager
                        icon={
                            showMoreContent
                                ? 'SvgChevronUp24'
                                : 'SvgChevronDown24'
                        }
                        size={24}
                    />
                </MoreTextWrapper>
            )}
        </ShowMoreWrapperElement>
    );
};
